import { CapacitorPasskey } from "@capgo/capacitor-passkey";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

/**
 * Native passkey (WebAuthn) bridge for the Capacitor build — DEVOP-5462 PoC.
 *
 * WebAuthn is unavailable inside the Capacitor WebView: on iOS WKWebView
 * `navigator.credentials.create/get` never resolves (no browser entitlement),
 * and Android's System WebView has no WebAuthn at all. `@capgo/capacitor-passkey`
 * patches `navigator.credentials.create/get` to forward `publicKey` requests to
 * the native ASAuthorization (iOS) / Credential Manager (Android) APIs, so the
 * existing Amplify-driven ceremony in `cognitoWebAuthnAuth.ts` works unchanged.
 *
 * Web is untouched: `installNativePasskeyShim()` is a no-op off-device.
 */

let nativePasskeyReady = false;

/**
 * Upper bound for the native passkey startup probe. `installNativePasskeyShim()`
 * is awaited before first render, so a plugin call that never resolves would
 * otherwise hang app bootstrap indefinitely. On timeout we treat passkeys as
 * unsupported rather than block startup.
 */
const PASSKEY_SHIM_TIMEOUT_MS = 3000;

/** Rejects if `operation` does not settle within `timeoutMs`. */
async function withTimeout<T>(operation: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Passkey native shim timed out after ${timeoutMs}ms`)),
      timeoutMs
    );
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Whether the native passkey shim is installed and the device reports passkey
 * support. Read synchronously by `isPasskeySupported()`; only ever `true` after
 * `installNativePasskeyShim()` resolves on a capable native device.
 */
export function isNativePasskeyReady(): boolean {
  return nativePasskeyReady;
}

/**
 * Installs the native WebAuthn shim and caches device passkey support. Must be
 * awaited during app bootstrap (before the login screen renders) so that
 * `isPasskeySupported()` returns the correct value on first paint.
 *
 * No-op on web. Failures are swallowed (logged) so a missing/old plugin simply
 * leaves passkeys unsupported rather than blocking app startup.
 */
export async function installNativePasskeyShim(): Promise<void> {
  if (!Platform.isMobile) {
    return;
  }

  try {
    // Reads `origin` / `autoShim` from the CapacitorPasskey config block in
    // mobile/capacitor.config.ts (encodes the HTTPS RP origin into the iOS
    // clientDataJSON, since the WebView runs at capacitor://localhost).
    // Bounded by a timeout so a hung plugin call can't block app bootstrap.
    await withTimeout(
      (async () => {
        await CapacitorPasskey.autoShimWebAuthn();
        const support = await CapacitorPasskey.isSupported();
        nativePasskeyReady = support.available;
      })(),
      PASSKEY_SHIM_TIMEOUT_MS
    );
  } catch (error: unknown) {
    nativePasskeyReady = false;
    logError(APP_V2_APP_EVENTS.PASSKEY_NATIVE_SHIM_FAILURE, { error });
  }
}
